/**
 * Security Compliance Dashboard
 * Provides real-time security compliance monitoring and reporting
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';
import EmergencyAccountRecovery from '../components/EmergencyAccountRecovery';
import UserBackupManager from '../components/UserBackupManager';
import './SecurityComplianceDashboard.css';

const SecurityComplianceDashboard = () => {
  const { t } = useTranslation();
  const [complianceData, setComplianceData] = useState({
    overallScore: 0,
    categories: {},
    recentEvents: [],
    vulnerabilities: [],
    recommendations: []
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showRecoveryTool, setShowRecoveryTool] = useState(false);
  const [showBackupManager, setShowBackupManager] = useState(false);

  useEffect(() => {
    loadComplianceData();
    // Removed auto-refresh to prevent annoying page reloads
    // Users can manually refresh if needed
  }, []);

  const loadComplianceData = async () => {
    try {
      setLoading(true);
      
      // Load security events
      const eventsQuery = query(
        collection(db, 'security_logs'),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      const eventsSnapshot = await getDocs(eventsQuery);
      const recentEvents = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate compliance score
      const complianceScore = calculateComplianceScore(recentEvents);
      
      // Get category breakdown
      const categories = getCategoryBreakdown(recentEvents);
      
      // Get vulnerabilities
      const vulnerabilities = getVulnerabilities(recentEvents);
      
      // Get recommendations
      const recommendations = getRecommendations(recentEvents, complianceScore);

      setComplianceData({
        overallScore: complianceScore,
        categories,
        recentEvents: recentEvents.slice(0, 10),
        vulnerabilities,
        recommendations
      });
      
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateComplianceScore = (events) => {
    // Base score
    let score = 100;
    
    // Deduct points for security events
    events.forEach(event => {
      switch (event.level) {
        case 'ERROR':
          score -= 10;
          break;
        case 'WARN':
          score -= 5;
          break;
        case 'INFO':
          score -= 1;
          break;
        default:
          break;
      }
    });
    
    // Ensure score doesn't go below 0
    return Math.max(0, score);
  };

  const getCategoryBreakdown = (events) => {
    const categories = {
      authentication: { count: 0, score: 100 },
      authorization: { count: 0, score: 100 },
      dataProtection: { count: 0, score: 100 },
      networkSecurity: { count: 0, score: 100 },
      applicationSecurity: { count: 0, score: 100 }
    };

    events.forEach(event => {
      const category = getEventCategory(event.category);
      if (categories[category]) {
        categories[category].count++;
        // Deduct score based on event severity
        if (event.level === 'ERROR') categories[category].score -= 10;
        else if (event.level === 'WARN') categories[category].score -= 5;
        else if (event.level === 'INFO') categories[category].score -= 1;
      }
    });

    // Ensure scores don't go below 0
    Object.keys(categories).forEach(key => {
      categories[key].score = Math.max(0, categories[key].score);
    });

    return categories;
  };

  const getEventCategory = (category) => {
    switch (category) {
      case 'AUTHENTICATION':
        return 'authentication';
      case 'AUTHORIZATION':
        return 'authorization';
      case 'DATA':
        return 'dataProtection';
      case 'NETWORK':
        return 'networkSecurity';
      case 'APPLICATION':
        return 'applicationSecurity';
      default:
        return 'applicationSecurity';
    }
  };

  const getVulnerabilities = (events) => {
    return events
      .filter(event => 
        event.eventType === 'INJECTION_ATTEMPT' || 
        event.eventType === 'BRUTE_FORCE_ATTEMPT' ||
        event.eventType === 'UNAUTHORIZED_ACCESS'
      )
      .slice(0, 5);
  };

  const getRecommendations = (events, score) => {
    const recommendations = [];
    
    if (score < 80) {
      recommendations.push({
        priority: 'HIGH',
        title: 'Security Score Below Threshold',
        description: 'Overall security score is below 80%. Review recent security events and implement additional controls.',
        action: 'Review security logs and implement recommendations'
      });
    }
    
    const authEvents = events.filter(e => e.category === 'AUTHENTICATION');
    if (authEvents.length > 10) {
      recommendations.push({
        priority: 'MEDIUM',
        title: 'High Authentication Events',
        description: 'Unusual number of authentication events detected. Review for potential security issues.',
        action: 'Review authentication logs and consider additional monitoring'
      });
    }
    
    const failedLogins = events.filter(e => e.eventType === 'LOGIN_FAILED');
    if (failedLogins.length > 5) {
      recommendations.push({
        priority: 'HIGH',
        title: 'Multiple Failed Login Attempts',
        description: 'Multiple failed login attempts detected. Consider implementing additional rate limiting.',
        action: 'Review failed login attempts and consider account lockout policies'
      });
    }
    
    return recommendations;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#27ae60';
    if (score >= 70) return '#f39c12';
    return '#e74c3c';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH': return '#e74c3c';
      case 'MEDIUM': return '#f39c12';
      case 'LOW': return '#3498db';
      default: return '#95a5a6';
    }
  };

  if (loading) {
    return (
      <div className="security-compliance-dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading security compliance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="security-compliance-dashboard">
      <div className="dashboard-header">
        <h1>Security Compliance Dashboard</h1>
        <div className="header-actions">
          <div className="action-buttons">
            <button 
              className="refresh-button"
              onClick={loadComplianceData}
              disabled={loading}
            >
              {loading ? '⏳ Refreshing...' : '🔄 Refresh Data'}
            </button>
            <button 
              className="recovery-tool-button"
              onClick={() => setShowRecoveryTool(!showRecoveryTool)}
            >
              🚨 Emergency Recovery
            </button>
            <button 
              className="backup-manager-button"
              onClick={() => setShowBackupManager(!showBackupManager)}
            >
              🔄 Backup Manager
            </button>
          </div>
          <div className="last-updated">
            Last updated: {lastUpdated?.toLocaleString()}
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Overall Score */}
        <div className="score-card">
          <h2>Overall Security Score</h2>
          <div className="score-circle">
            <div 
              className="score-value"
              style={{ color: getScoreColor(complianceData.overallScore) }}
            >
              {complianceData.overallScore}
            </div>
            <div className="score-label">/ 100</div>
          </div>
          <div className="score-status">
            {complianceData.overallScore >= 90 ? 'Excellent' : 
             complianceData.overallScore >= 70 ? 'Good' : 'Needs Attention'}
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="categories-card">
          <h2>Security Categories</h2>
          <div className="categories-list">
            {Object.entries(complianceData.categories).map(([category, data]) => (
              <div key={category} className="category-item">
                <div className="category-name">
                  {category.charAt(0).toUpperCase() + category.slice(1).replace(/([A-Z])/g, ' $1')}
                </div>
                <div className="category-score">
                  <div 
                    className="score-bar"
                    style={{ 
                      width: `${data.score}%`,
                      backgroundColor: getScoreColor(data.score)
                    }}
                  ></div>
                  <span className="score-text">{data.score}</span>
                </div>
                <div className="category-count">{data.count} events</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Events */}
        <div className="events-card">
          <h2>Recent Security Events</h2>
          <div className="events-list">
            {complianceData.recentEvents.map((event, index) => (
              <div key={index} className="event-item">
                <div className="event-time">
                  {new Date(event.timestamp?.toDate()).toLocaleString()}
                </div>
                <div className="event-details">
                  <div className="event-type">{event.eventType}</div>
                  <div className="event-description">{event.description}</div>
                </div>
                <div className={`event-level level-${event.level?.toLowerCase()}`}>
                  {event.level}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Vulnerabilities */}
        <div className="vulnerabilities-card">
          <h2>Security Vulnerabilities</h2>
          <div className="vulnerabilities-list">
            {complianceData.vulnerabilities.length === 0 ? (
              <div className="no-vulnerabilities">No vulnerabilities detected</div>
            ) : (
              complianceData.vulnerabilities.map((vuln, index) => (
                <div key={index} className="vulnerability-item">
                  <div className="vulnerability-type">{vuln.eventType}</div>
                  <div className="vulnerability-description">{vuln.description}</div>
                  <div className="vulnerability-time">
                    {new Date(vuln.timestamp?.toDate()).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recommendations */}
        <div className="recommendations-card">
          <h2>Security Recommendations</h2>
          <div className="recommendations-list">
            {complianceData.recommendations.length === 0 ? (
              <div className="no-recommendations">No recommendations at this time</div>
            ) : (
              complianceData.recommendations.map((rec, index) => (
                <div key={index} className="recommendation-item">
                  <div className="recommendation-header">
                    <div 
                      className="recommendation-priority"
                      style={{ backgroundColor: getPriorityColor(rec.priority) }}
                    >
                      {rec.priority}
                    </div>
                    <div className="recommendation-title">{rec.title}</div>
                  </div>
                  <div className="recommendation-description">{rec.description}</div>
                  <div className="recommendation-action">{rec.action}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      
      {/* Emergency Account Recovery Tool */}
      {showRecoveryTool && (
        <div className="recovery-tool-section">
          <EmergencyAccountRecovery />
        </div>
      )}
      
      {/* User Backup Manager */}
      {showBackupManager && (
        <div className="backup-manager-section">
          <UserBackupManager />
        </div>
      )}
    </div>
  );
};

export default SecurityComplianceDashboard;
